import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Search, Send, Inbox as InboxIcon, SendHorizontal, FileEdit, Trash2, Mail, Zap, ExternalLink, Users, RefreshCcw } from 'lucide-react';
import { format } from 'date-fns';
import ShareViaEmail from '@/components/ShareViaEmail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InboxPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sendMode, setSendMode] = useState('internal');
  const [composeForm, setComposeForm] = useState({ to_user_id: '', to_email: '', subject: '', content: '', is_draft: false });
  const [digests, setDigests] = useState([]);
  const [selectedDigest, setSelectedDigest] = useState(null);
  const [sentEmails, setSentEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [digestShareOpen, setDigestShareOpen] = useState(false);

  useEffect(() => {
    if (activeTab === 'digests') {
      fetchDigests();
    } else if (activeTab === 'sent_emails') {
      fetchSentEmails();
    } else {
      fetchMessages();
    }
    fetchUsers();
  }, [activeTab]);

  const fetchDigests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/digest/list`, { headers: { Authorization: `Bearer ${token}` } });
      setDigests(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchSentEmails = async ({ withStatus = true } = {}) => {
    setLoading(true);
    try {
      const url = withStatus ? `${API}/emails/sent?include_status=true` : `${API}/emails/sent`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setSentEmails(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      let endpoint = '/messages/inbox';
      if (activeTab === 'sent') endpoint = '/messages/sent';
      if (activeTab === 'drafts') endpoint = '/messages/drafts';
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => u.user_id !== user?.user_id));
    } catch { /* silent */ }
  };

  const handleSend = async (isDraft = false) => {
    if (sendMode === 'external') {
      if (!composeForm.to_email || !composeForm.subject) {
        toast.error('Email address and subject are required');
        return;
      }
      setSending(true);
      try {
        await axios.post(`${API}/emails/send`, {
          to_email: composeForm.to_email,
          subject: composeForm.subject,
          content: composeForm.content
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Email sent to ${composeForm.to_email}`);
        setIsComposeOpen(false);
        resetComposeForm();
        if (activeTab === 'sent_emails') fetchSentEmails();
      } catch (error) {
        const detail = error.response?.data?.detail || 'Failed to send email';
        toast.error(detail);
      } finally {
        setSending(false);
      }
    } else {
      if (!composeForm.to_user_id || !composeForm.subject) {
        toast.error('Recipient and subject are required');
        return;
      }
      setSending(true);
      try {
        await axios.post(`${API}/messages`, {
          to_user_id: composeForm.to_user_id,
          subject: composeForm.subject,
          body: composeForm.content,
          is_draft: isDraft
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(isDraft ? 'Draft saved' : 'Message sent');
        setIsComposeOpen(false);
        resetComposeForm();
        fetchMessages();
      } catch {
        toast.error('Failed to send message');
      } finally {
        setSending(false);
      }
    }
  };

  const resetComposeForm = () => {
    setComposeForm({ to_user_id: '', to_email: '', subject: '', content: '', is_draft: false });
    setSendMode('internal');
  };

  const handleDelete = async (messageId) => {
    if (!confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      fetchMessages();
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredMessages = messages.filter(m =>
    !search ||
    m.subject?.toLowerCase().includes(search.toLowerCase()) ||
    m.body?.toLowerCase().includes(search.toLowerCase()) ||
    m.content?.toLowerCase().includes(search.toLowerCase())
  );

  const sidebarItems = [
    { key: 'inbox', label: 'Inbox', icon: InboxIcon, badge: messages.filter(m => !m.is_read).length },
    { key: 'sent', label: 'Sent', icon: SendHorizontal },
    { key: 'sent_emails', label: 'Sent Emails', icon: Mail },
    { key: 'drafts', label: 'Drafts', icon: FileEdit },
    { key: 'digests', label: 'AI Digests', icon: Zap },
  ];

  return (
    <div data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Inbox</h1>
          <p className="text-[#4B5563] text-sm">Messages, emails, and digests</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { resetComposeForm(); setIsComposeOpen(true); }} data-testid="compose-btn">
          <Send className="w-4 h-4 mr-2" />
          Compose
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-2">
              <nav className="space-y-1">
                {sidebarItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setActiveTab(item.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                        activeTab === item.key ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F3F4F6]'
                      }`}
                      data-testid={`${item.key}-tab`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                      {item.badge > 0 && activeTab === item.key && (
                        <Badge className="ml-auto bg-[#FF3B30]">{item.badge}</Badge>
                      )}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          {activeTab === 'digests' ? (
            <DigestsView digests={digests} loading={loading} selectedDigest={selectedDigest} setSelectedDigest={setSelectedDigest} onShare={() => setDigestShareOpen(true)} />
          ) : activeTab === 'sent_emails' ? (
            <SentEmailsView emails={sentEmails} loading={loading} onRefresh={fetchSentEmails} />
          ) : (
            <MessagesView
              messages={filteredMessages} loading={loading} search={search} setSearch={setSearch}
              activeTab={activeTab} users={users} onView={(msg) => { setSelectedMessage(msg); setIsViewOpen(true); }}
              onDelete={handleDelete} getInitials={getInitials}
            />
          )}
        </div>
      </div>

      {/* Compose Dialog */}
      <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
        <DialogContent className="max-w-xl" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle>Compose</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Send mode toggle */}
            <div className="flex gap-2 p-1 bg-[#F3F4F6] rounded-md" data-testid="send-mode-toggle">
              <button
                onClick={() => setSendMode('internal')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded transition-colors ${
                  sendMode === 'internal' ? 'bg-white text-[#002FA7] shadow-sm' : 'text-[#4B5563]'
                }`}
                data-testid="mode-internal-btn"
              >
                <Users className="w-4 h-4" />
                Internal Message
              </button>
              <button
                onClick={() => setSendMode('external')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded transition-colors ${
                  sendMode === 'external' ? 'bg-white text-[#002FA7] shadow-sm' : 'text-[#4B5563]'
                }`}
                data-testid="mode-external-btn"
              >
                <ExternalLink className="w-4 h-4" />
                External Email
              </button>
            </div>

            {sendMode === 'internal' ? (
              <div>
                <Label>To (Team Member) *</Label>
                <Select value={composeForm.to_user_id} onValueChange={(v) => setComposeForm({ ...composeForm, to_user_id: v })}>
                  <SelectTrigger data-testid="compose-to-select">
                    <SelectValue placeholder="Select recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>To (Email Address) *</Label>
                <Input
                  type="email"
                  value={composeForm.to_email}
                  onChange={(e) => setComposeForm({ ...composeForm, to_email: e.target.value })}
                  placeholder="recipient@example.com"
                  data-testid="compose-to-email"
                />
                <p className="text-xs text-[#9CA3AF] mt-1">Email will be sent from KStreak</p>
              </div>
            )}

            <div>
              <Label>Subject *</Label>
              <Input
                value={composeForm.subject}
                onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                placeholder="Enter subject"
                data-testid="compose-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={6}
                value={composeForm.content}
                onChange={(e) => setComposeForm({ ...composeForm, content: e.target.value })}
                placeholder="Write your message..."
                data-testid="compose-content"
              />
            </div>
          </div>
          <DialogFooter>
            {sendMode === 'internal' && (
              <Button variant="outline" onClick={() => handleSend(true)} disabled={sending} data-testid="save-draft-btn">
                Save Draft
              </Button>
            )}
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => handleSend(false)} disabled={sending} data-testid="send-message-btn">
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sendMode === 'external' ? 'Send Email' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Message Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-xl" data-testid="view-message-dialog">
          <DialogHeader>
            <DialogTitle>{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-[#E5E7EB]">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-[#002FA7] text-white">
                    {getInitials(selectedMessage.from_user_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedMessage.from_user_name}</p>
                  <p className="text-sm text-[#9CA3AF]">
                    {format(new Date(selectedMessage.created_at), 'MMMM d, yyyy at h:mm a')}
                  </p>
                </div>
              </div>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{selectedMessage.body || selectedMessage.content}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedDigest && (
        <ShareViaEmail
          open={digestShareOpen}
          onOpenChange={setDigestShareOpen}
          subject="KStreak Weekly Digest"
          content={selectedDigest.content}
          contentType="Digest"
        />
      )}
    </div>
  );
};

// Digests sub-view
const DigestsView = ({ digests, loading, selectedDigest, setSelectedDigest, onShare }) => (
  <Card className="border-[#E5E7EB]">
    <CardHeader className="border-b border-[#E5E7EB]">
      <CardTitle className="text-base flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#002FA7]" />
        AI Weekly Digests
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : digests.length === 0 ? (
        <div className="text-center py-12 text-[#9CA3AF]">
          <Zap className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" />
          <p className="text-sm">No digests yet. Generate one from your Dashboard.</p>
        </div>
      ) : selectedDigest ? (
        <div className="p-4" data-testid="digest-detail">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={() => setSelectedDigest(null)} className="text-[#002FA7]">Back to list</Button>
            <Button variant="outline" size="sm" onClick={onShare} data-testid="share-digest-inbox-btn">
              <Mail className="w-3.5 h-3.5 mr-1.5" />Share via Email
            </Button>
          </div>
          <div className="flex gap-4 mb-4">
            {[
              { label: 'Articles', value: selectedDigest.stats?.articles_count || 0 },
              { label: 'Tasks', value: selectedDigest.stats?.tasks_count || 0 },
              { label: 'Journals', value: selectedDigest.stats?.journals_count || 0 },
              { label: 'Transfers', value: selectedDigest.stats?.transfers_count || 0 },
            ].map((s, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{s.value} {s.label}</Badge>
            ))}
          </div>
          <div className="prose prose-sm max-w-none text-sm text-[#4B5563] [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-[#0A0A0A] [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-[#0A0A0A] [&_strong]:text-[#0A0A0A]">
            {selectedDigest.content.split('\n').map((line, i) => {
              if (!line.trim()) return <br key={i} />;
              if (line.startsWith('### ')) return <h3 key={i}>{line.slice(4)}</h3>;
              if (line.startsWith('## ')) return <h2 key={i}>{line.slice(3)}</h2>;
              if (line.startsWith('- ')) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7]">-</span><span>{line.slice(2)}</span></div>;
              if (line.match(/^\d+\.\s/)) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7] font-medium">{line.match(/^\d+/)[0]}.</span><span>{line.replace(/^\d+\.\s/, '')}</span></div>;
              return <p key={i}>{line}</p>;
            })}
          </div>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          {digests.map((d) => (
            <div key={d.digest_id} className="p-4 hover:bg-[#F9FAFB] cursor-pointer flex items-center gap-4" onClick={() => setSelectedDigest(d)} data-testid={`digest-${d.digest_id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#002FA7]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-[#0A0A0A]">Weekly Digest</p>
                <p className="text-xs text-[#9CA3AF]">{d.stats?.articles_count || 0} articles, {d.stats?.tasks_count || 0} tasks, {d.stats?.journals_count || 0} journals</p>
              </div>
              <span className="text-xs text-[#9CA3AF] flex-shrink-0">{format(new Date(d.created_at), 'MMM d, h:mm a')}</span>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// Sent Emails sub-view
const DELIVERY_STATUS_STYLES = {
  delivered:        { label: 'Delivered',      bg: '#34C759', icon: '✓', help: 'Landed at the recipient server' },
  sent:             { label: 'Sent',           bg: '#9CA3AF', icon: '→', help: 'Handed to Resend, awaiting recipient server' },
  queued:           { label: 'Queued',         bg: '#9CA3AF', icon: '→', help: 'Queued at Resend' },
  bounced:          { label: 'Bounced',        bg: '#FF3B30', icon: '!', help: 'Recipient server rejected the email' },
  delivery_delayed: { label: 'Delayed',        bg: '#FF9500', icon: '⏱', help: 'Recipient server asked Resend to retry' },
  complained:       { label: 'Spam complaint', bg: '#FF3B30', icon: '⚠', help: 'Recipient marked this as spam' },
  opened:           { label: 'Opened',         bg: '#002FA7', icon: '👁', help: 'Recipient opened the email' },
  clicked:          { label: 'Clicked',        bg: '#002FA7', icon: '🔗', help: 'Recipient clicked a link' },
  unknown:          { label: 'Unknown',        bg: '#9CA3AF', icon: '?', help: 'Resend lookup failed' },
  no_id:            { label: 'No tracking',    bg: '#9CA3AF', icon: '—', help: 'Sent before tracking was enabled' },
};

const DeliveryBadge = ({ status }) => {
  const s = DELIVERY_STATUS_STYLES[status] || DELIVERY_STATUS_STYLES.unknown;
  return (
    <span
      title={s.help}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
      style={{ backgroundColor: s.bg }}
      data-testid={`delivery-badge-${status}`}
    >
      <span aria-hidden>{s.icon}</span>{s.label}
    </span>
  );
};

const SentEmailsView = ({ emails, loading, onRefresh }) => (
  <Card className="border-[#E5E7EB]">
    <CardHeader className="border-b border-[#E5E7EB] flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base flex items-center gap-2">
        <Mail className="w-4 h-4 text-[#002FA7]" />
        Sent Emails (External)
      </CardTitle>
      <Button
        size="sm" variant="ghost" onClick={() => onRefresh && onRefresh()}
        data-testid="refresh-delivery-status-btn" disabled={loading}
      >
        <RefreshCcw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
        Refresh delivery status
      </Button>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12 text-[#9CA3AF]">
          <Mail className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" />
          <p className="text-sm">No external emails sent yet.</p>
          <p className="text-xs mt-1">Use Compose → External Email to send.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          {emails.map((e) => (
            <div key={e.email_id} className="p-4 flex items-start gap-4" data-testid={`sent-email-${e.email_id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#34C759]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#34C759]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-medium text-sm text-[#0A0A0A] truncate">{e.subject}</span>
                  {e.delivery_status && <DeliveryBadge status={e.delivery_status} />}
                </div>
                <p className="text-xs text-[#9CA3AF]">To: {e.to_email}{e.from_email ? ` · From: ${e.from_email}` : ''}</p>
                <p className="text-sm text-[#4B5563] truncate mt-1">{e.content}</p>
                {e.delivery_error && (
                  <p className="text-[10px] text-[#FF3B30] mt-1 break-words" data-testid={`delivery-error-${e.email_id}`}>
                    {e.delivery_error}
                  </p>
                )}
              </div>
              <span className="text-xs text-[#9CA3AF] flex-shrink-0">{format(new Date(e.created_at), 'MMM d, h:mm a')}</span>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// Messages sub-view
const MessagesView = ({ messages, loading, search, setSearch, activeTab, users, onView, onDelete, getInitials }) => (
  <Card className="border-[#E5E7EB]">
    <CardHeader className="border-b border-[#E5E7EB]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
        <Input placeholder="Search messages..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="message-search" />
      </div>
    </CardHeader>
    <CardContent className="p-0">
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 text-[#9CA3AF]">No messages</div>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          {messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`p-4 hover:bg-[#F9FAFB] cursor-pointer flex items-start gap-4 ${!msg.is_read ? 'bg-[#F3F4F6]' : ''}`}
              onClick={() => onView(msg)}
              data-testid={`message-${msg.message_id}`}
            >
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarFallback className="bg-[#002FA7] text-white text-sm">
                  {getInitials(activeTab === 'sent' ? (users.find(u => u.user_id === msg.to_user_id)?.name) : msg.from_user_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-medium truncate ${!msg.is_read ? 'text-[#0A0A0A]' : 'text-[#4B5563]'}`}>
                    {activeTab === 'sent' ? users.find(u => u.user_id === msg.to_user_id)?.name || 'Unknown' : msg.from_user_name}
                  </span>
                  <span className="text-xs text-[#9CA3AF] flex-shrink-0">{format(new Date(msg.created_at), 'MMM d, h:mm a')}</span>
                </div>
                <p className={`text-sm truncate ${!msg.is_read ? 'font-medium' : ''}`}>{msg.subject}</p>
                <p className="text-sm text-[#9CA3AF] truncate">{msg.body || msg.content}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(msg.message_id); }} className="text-[#FF3B30] flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default InboxPage;
